import { faGripVertical, faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { Card, Col, FormGroup, FormLabel, FormSelect, Row } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import FormControl from "react-bootstrap/FormControl";
import Modal from "react-bootstrap/Modal";
import BootstrapTable from "react-bootstrap/Table";
import { FormattedMessage, useIntl } from "react-intl";

const ASSIGNED_ROLES = "assigned";

const CreateEditRoleDialog = ({ role, propertyChange, permissionList, onCancel, saveRole, roleList }) => {
  const intl = useIntl();
  const [dragTarget, setDragTarget] = useState();

  const newMode = role.id === undefined;

  const roleExists = !!(newMode && roleList && roleList.find((r) => r.name === role.name));

  const rolePermissionNames = role.permissions.map((perm) => perm.name);
  const availablePermissionList = permissionList.filter((permission) => !rolePermissionNames.includes(permission.name));

  const isFormValid =
    !roleExists &&
    role.name?.trim().length > 0 &&
    role.titleDe?.trim().length > 0 &&
    role.titleEn?.trim().length > 0 &&
    rolePermissionNames?.length > 0;

  const moveRole = (permission, add) => {
    const perms = new Map();
    role.permissions.forEach((e) => perms.set(e.id, e));

    if (add) {
      perms.set(permission.id, permission);
    } else {
      perms.delete(permission.id);
    }

    propertyChange("permissions", [...perms.values()]);
  };

  const handleDragStart = (role) => {
    setDragTarget(role);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    if (dragTarget) {
      moveRole(dragTarget, event.currentTarget.id === ASSIGNED_ROLES);
      setDragTarget(null);
      event.stopPropagation();
      event.preventDefault();
    }
  };

  return (
    <Modal show size="xl" scrollable>
      <Modal.Header>
        <Modal.Title>
          <FormattedMessage id={`role.management.${newMode ? "create.new" : "edit"}.role`} />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormGroup  className="mb-3">
          <FormLabel>
            <FormattedMessage id="role.management.role.name" /> *
          </FormLabel>
          <FormControl
            name="name"
            disabled={!!role.id}
            required
            isInvalid={role.name?.trim() === "" || roleExists}
            defaultValue={role.name || ""}
            onBlur={(e) => propertyChange(e.target.name, e.target.value.trim())}
          />
          {role.name?.trim() === "" && (
            <FormControl.Feedback type="invalid">
              <FormattedMessage id="app.common.field.error.required" />
            </FormControl.Feedback>
          )}
          {roleExists && (
            <FormControl.Feedback type="invalid">
              <FormattedMessage id="user.management.create.new.role.exists.warning" />
            </FormControl.Feedback>
          )}
        </FormGroup>
        <FormGroup  className="mb-3">
          <FormLabel>
            <FormattedMessage id="role.management.role.title.german" /> *
          </FormLabel>
          <FormControl
            name="titleDe"
            required
            isInvalid={role.titleDe?.trim() === ""}
            defaultValue={role.titleDe || ""}
            onBlur={(e) => propertyChange(e.target.name, e.target.value.trim())}
          />
          <FormControl.Feedback type="invalid">
            <FormattedMessage id="app.common.field.error.required" />
          </FormControl.Feedback>
        </FormGroup>
        <FormGroup  className="mb-3">
          <FormLabel>
            <FormattedMessage id="role.management.role.title.english" /> *
          </FormLabel>
          <FormControl
            name="titleEn"
            required
            isInvalid={role.titleEn?.trim() === ""}
            defaultValue={role.titleEn || ""}
            onBlur={(e) => propertyChange(e.target.name, e.target.value.trim())}
          />
          <FormControl.Feedback type="invalid">
            <FormattedMessage id="app.common.field.error.required" />
          </FormControl.Feedback>
        </FormGroup>
        <FormGroup  className="mb-3">
          <FormLabel>
            <FormattedMessage id="role.management.role.scope" /> *
          </FormLabel>
          <FormSelect
            name="scope"
            required     
            onChange={(e) => propertyChange(e.target.name, e.target.value)}
            defaultValue={role.scope || ""}
          >
            <option value="fundingarea">{intl.formatMessage({ id: "master.data.management.funding.area" })}</option>
            <option value="program">{intl.formatMessage({ id: "master.data.management.program" })}</option>
            <option value="programInstance">
              {intl.formatMessage({ id: "master.data.management.program.instance" })}
            </option>
            <option value="project">{intl.formatMessage({ id: "master.data.management.project" })}</option>
          </FormSelect>
        </FormGroup>
        <Card>
          <Card.Header>
            <Card.Title>
              <FormattedMessage id="user.management.table.permissions" />
            </Card.Title>
            <Card.Subtitle>
              <FormattedMessage id="user.management.table.permissions.hint" />
            </Card.Subtitle>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col sm={12} md={6}>
                <FormGroup className="dropzone mb-3" onDragOver={(e) => handleDragOver(e)} onDrop={(e) => handleDrop(e)}>
                  <BootstrapTable striped borderless hover>
                    <thead>
                      <tr>
                        <th colSpan={3} className="bg-light">
                          <FormattedMessage id="role.management.available.permissions" />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {availablePermissionList &&
                        availablePermissionList.map((permission) => (
                          <tr key={permission.id} draggable onDragStart={(e) => handleDragStart(permission, true, e)}>
                            <td className="shrink">
                              <Button variant="link" size="sm">
                                <FontAwesomeIcon icon={faGripVertical} />
                              </Button>
                            </td>
                            <td>
                              <FormattedMessage id={`user.management.roles.${permission.name}`} />
                            </td>
                            <td className="actions">
                              <Button
                                variant="link"
                                size="sm"
                                title={intl.formatMessage({ id: "app.common.action.add" })}
                                onClick={(e) => {
                                  moveRole(permission, true);
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                              >
                                <FontAwesomeIcon icon={faPlus} />
                              </Button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </BootstrapTable>
                </FormGroup>
              </Col>
              <Col sm={12} md={6} className="border-left">
                <FormGroup
                  id={ASSIGNED_ROLES}
                  className="dropzone"
                  onDragOver={(e) => handleDragOver(e)}
                  onDrop={(e) => handleDrop(e)}
                >
                  <BootstrapTable striped hover borderless responsive>
                    <thead>
                      <tr>
                        <th colSpan={3} className="bg-light">
                          <FormattedMessage id="role.management.assigned.permissions" />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {role.permissions &&
                        role.permissions.map((permission) => (
                          <tr key={permission.id} draggable onDragStart={(e) => handleDragStart(permission, false, e)}>
                            <td className="shrink">
                              <Button size="sm" variant="link" style={{ cursor: "grab" }}>
                                <FontAwesomeIcon icon={faGripVertical} />
                              </Button>
                            </td>
                            <td>
                              <FormattedMessage id={`user.management.roles.${permission.name}`} />
                            </td>
                            <td className="actions">
                              <Button
                                variant="link"
                                size="sm"
                                title={intl.formatMessage({ id: "app.common.action.delete" })}
                                onClick={(e) => {
                                  moveRole(permission, false);
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                              >
                                <FontAwesomeIcon icon={faMinus} />
                              </Button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </BootstrapTable>
                </FormGroup>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel}>
          <FormattedMessage id="app.common.action.cancel" />
        </Button>
        <Button onClick={() => saveRole()} disabled={!isFormValid}>
          <FormattedMessage id="app.common.action.save" />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

CreateEditRoleDialog.propTypes = {
  show: PropTypes.bool,
  role: PropTypes.object.isRequired,
  permissionList: PropTypes.arrayOf(PropTypes.object).isRequired,

  onCancel: PropTypes.func.isRequired,
  propertyChange: PropTypes.func.isRequired,
  saveRole: PropTypes.func.isRequired,

  editUser: PropTypes.func,
  createNewUser: PropTypes.func,
  createNewUserDialog: PropTypes.bool,
  editUserDialog: PropTypes.bool,
  active: PropTypes.bool
};

export default CreateEditRoleDialog;
